import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Trash2, Send, Plus, Bell } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  listDomainAlertRules,
  upsertDomainAlertRule,
  deleteDomainAlertRule,
  testDomainAlertRule,
} from "@/lib/domain-alert-rules.functions";

export const Route = createFileRoute("/_authenticated/domain-alert-rules")({
  component: DomainAlertRulesPage,
  head: () => ({ meta: [{ title: "Domain Alert Rules — InboxPulse" }] }),
});

type Form = {
  id?: string;
  name: string;
  domain_id: string;
  score_drop_threshold: number;
  score_below: string;
  on_blacklist: boolean;
  enabled: boolean;
  cooldown_minutes: number;
  telegram_chat_id: string;
};

const empty: Form = {
  name: "",
  domain_id: "",
  score_drop_threshold: 10,
  score_below: "",
  on_blacklist: true,
  enabled: true,
  cooldown_minutes: 60,
  telegram_chat_id: "",
};

function DomainAlertRulesPage() {
  const qc = useQueryClient();
  const list = useServerFn(listDomainAlertRules);
  const upsert = useServerFn(upsertDomainAlertRule);
  const del = useServerFn(deleteDomainAlertRule);
  const test = useServerFn(testDomainAlertRule);

  const rulesQ = useQuery({ queryKey: ["domain-alert-rules"], queryFn: () => list() });
  const domainsQ = useQuery({
    queryKey: ["domains-lite"],
    queryFn: async () =>
      (await supabase.from("domains").select("id, domain").order("domain")).data ?? [],
  });

  const [form, setForm] = useState<Form>(empty);

  const save = useMutation({
    mutationFn: async (f: Form) =>
      upsert({
        data: {
          id: f.id,
          name: f.name,
          domain_id: f.domain_id ? f.domain_id : null,
          score_drop_threshold: Number(f.score_drop_threshold),
          score_below: f.score_below === "" ? null : Number(f.score_below),
          on_blacklist: f.on_blacklist,
          enabled: f.enabled,
          cooldown_minutes: Number(f.cooldown_minutes),
          telegram_chat_id: f.telegram_chat_id || null,
        },
      }),
    onSuccess: () => {
      toast.success("Regel gespeichert");
      setForm(empty);
      qc.invalidateQueries({ queryKey: ["domain-alert-rules"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Regel gelöscht");
      qc.invalidateQueries({ queryKey: ["domain-alert-rules"] });
    },
  });

  const fireTest = useMutation({
    mutationFn: async (id: string) => test({ data: { id } }),
    onSuccess: (r) => {
      const res = Array.isArray(r) ? r[0] : null;
      if (!res) return toast.info("Test ausgeführt");
      const ch = res.channels;
      toast.success(`Test gefeuert — Telegram:${ch?.telegram ?? "-"} App:${ch?.inapp ?? "-"}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rules = (rulesQ.data ?? []) as Array<{
    id: string;
    name: string;
    domain_id: string | null;
    score_drop_threshold: number;
    score_below: number | null;
    on_blacklist: boolean;
    enabled: boolean;
    cooldown_minutes: number;
    telegram_chat_id: string | null;
    last_fired_at: string | null;
  }>;
  const domains = domainsQ.data ?? [];

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Domain Alert Rules"
        description="Telegram-Alerts bei Reputations-Abfall oder neuen Blacklist-Listings."
      />

      <HelpPanel
        title="Hilfe: Domain-spezifische Alarme"
        what={
          <>
            Hier setzt du Alarm-Regeln, die nur für <b>bestimmte Domains</b> gelten — z.B. "Bei{" "}
            <code>shop.deinefirma.de</code> sofort warnen, wenn auf einer Blacklist".
          </>
        }
        why={
          <>
            Manche Domains sind wichtiger als andere (z.B. deine Haupt-Verkaufsdomain). Für die
            kannst du strengere Regeln setzen.
          </>
        }
        how={[
          <>
            <b>Neue Regel</b> + Domain auswählen.
          </>,
          <>Auslöser: Reputations-Abfall oder Blacklist-Eintrag.</>,
          <>Empfänger eintragen, speichern.</>,
        ]}
        tips={[<>Für jede Geld-bringende Domain eine eigene Regel — die darf nie kaputt sein.</>]}
        glossary={[{ term: "Listing", def: "Eintrag auf einer Blacklist." }]}
      />

      <Card className="p-5 bg-card/60 border-border space-y-4">
        <div className="flex items-center gap-2 font-medium">
          <Plus className="size-4" /> {form.id ? "Regel bearbeiten" : "Neue Regel"}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="z. B. Prod-Domains Score-Drop"
            />
          </div>
          <div>
            <Label>Domain</Label>
            <select
              className="w-full h-10 px-3 rounded-md bg-background border border-input text-sm"
              value={form.domain_id}
              onChange={(e) => setForm({ ...form, domain_id: e.target.value })}
            >
              <option value="">Alle Domains</option>
              {domains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.domain}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Score-Drop ≥ (Punkte)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={form.score_drop_threshold}
              onChange={(e) => setForm({ ...form, score_drop_threshold: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label>Score unter (optional)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={form.score_below}
              onChange={(e) => setForm({ ...form, score_below: e.target.value })}
              placeholder="z. B. 50"
            />
          </div>
          <div>
            <Label>Cooldown (Min)</Label>
            <Input
              type="number"
              min={0}
              value={form.cooldown_minutes}
              onChange={(e) => setForm({ ...form, cooldown_minutes: Number(e.target.value) })}
            />
          </div>
          <div className="flex items-end gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.on_blacklist}
                onCheckedChange={(v) => setForm({ ...form, on_blacklist: v })}
              />
              Bei neuer Blacklist
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              />
              Aktiv
            </label>
          </div>
          <div className="md:col-span-2">
            <Label>Telegram Chat ID</Label>
            <Input
              value={form.telegram_chat_id}
              onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
              placeholder="-1001234567890"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leer lassen, um den globalen Telegram-Chat aus den Alarm-Einstellungen zu verwenden.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save.mutate(form)} disabled={!form.name || save.isPending}>
            {form.id ? "Speichern" : "Anlegen"}
          </Button>
          {form.id && (
            <Button variant="outline" onClick={() => setForm(empty)}>
              Abbrechen
            </Button>
          )}
        </div>
      </Card>

      <div className="space-y-2">
        {rules.length === 0 ? (
          <Card className="p-12 text-center bg-card/40 border-dashed">
            <Bell className="size-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">Noch keine Regeln</p>
            <p className="text-sm text-muted-foreground">Lege oben deine erste Alert-Regel an.</p>
          </Card>
        ) : (
          rules.map((r) => {
            const domainName = r.domain_id
              ? (domains.find((d) => d.id === r.domain_id)?.domain ?? "—")
              : "Alle Domains";
            return (
              <Card key={r.id} className="p-4 bg-card/60 border-border flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{r.name}</span>
                    {!r.enabled && <Badge variant="outline">deaktiviert</Badge>}
                    <Badge variant="secondary">{domainName}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                    <span>Drop ≥ {r.score_drop_threshold}</span>
                    {r.score_below != null && <span>• Score &lt; {r.score_below}</span>}
                    {r.on_blacklist && <span>• Blacklist</span>}
                    <span>• Cooldown {r.cooldown_minutes}m</span>
                    {r.telegram_chat_id && <span>• Telegram</span>}
                    {r.last_fired_at && (
                      <span>• Zuletzt: {new Date(r.last_fired_at).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fireTest.mutate(r.id)}
                  disabled={fireTest.isPending}
                >
                  <Send className="size-3.5 mr-1" /> Test
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setForm({
                      id: r.id,
                      name: r.name,
                      domain_id: r.domain_id ?? "",
                      score_drop_threshold: r.score_drop_threshold,
                      score_below: r.score_below == null ? "" : String(r.score_below),
                      on_blacklist: r.on_blacklist,
                      enabled: r.enabled,
                      cooldown_minutes: r.cooldown_minutes,
                      telegram_chat_id: r.telegram_chat_id ?? "",
                    })
                  }
                >
                  Bearbeiten
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove.mutate(r.id)}
                  disabled={remove.isPending}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
